import React from 'react';
import styled from 'styled-components';
import { User, MessageCircle, Mail, Phone, Clock } from 'react-feather';
import _ from 'lodash';
import moment from 'moment';

import { colors } from '../ui-elements/Main/MainTheme';
import { useNavigationActions } from '@/lib/navigation';

export interface SearchResult {
  type: string;
  id: string;
  data: any;
}

export interface SearchResultCardProps {
  subdomain: string;
  result: SearchResult;
  onNavigate?: () => void;
}

export const SearchResultCard: React.FC<SearchResultCardProps> = ({ subdomain, result, onNavigate }) => {
  const { navigate } = useNavigationActions();

  const createNavHandler = (path: string, params: Record<string, string>) => () => {
    onNavigate?.();
    navigate({ path, params });
  };

  if (result.type === 'sms_message' || result.type === 'mms_message') {
    return (
      <TicketResultCard onClick={createNavHandler(`/accounts/${subdomain}/tickets/${result.data.ticket.uuid}`, { matchId: result.data.event_id, matchAt: result.data.sent_at })}>
        <ResultHeader>
          <ResultLeft>
            <ContactName>
              {result.data.contact.name || result.data.contact.phone}
            </ContactName>
            {result.data.contact.company && (
              <Company>{_.truncate(result.data.contact.company, { length: 55 })}</Company>
            )}
          </ResultLeft>
          <ResultRight>
            <UnaddressedDot $unaddressed={result.data.ticket.unaddressed} />
            <StatusBadge $status={result.data.ticket.status || 'unknown'}>
              {result.data.ticket.status || 'Unknown'}
            </StatusBadge>
          </ResultRight>
        </ResultHeader>
        
        <Message>
          {result.data.sender_name && (
            <SenderName>{result.data.sender_name}: </SenderName>
          )}
          <span>{_.truncate(result.data.message, { length: 100 })}</span>
        </Message>
        
        <ResultFooter>
          <ResultType>
            <MessageCircle size={14} />
            <span>Message</span>
          </ResultType>
          {result.data.sent_at && (
            <Timestamp>
              <Clock size={12} />
              <span>{moment.unix(result.data.sent_at).fromNow()}</span>
            </Timestamp>
          )}
        </ResultFooter>
      </TicketResultCard>
    );
  }

  if (result.type === 'contact') {
    return (
      <ContactResultCard onClick={createNavHandler(`/accounts/${subdomain}/contacts/${result.id}`, {})}>
        <ResultHeader>
          <ResultLeft>
            <ContactName>
              {result.data.name || result.data.phone}
            </ContactName>
            {result.data.company && (
              <Company>{_.truncate(result.data.company, { length: 55 })}</Company>
            )}
          </ResultLeft>
        </ResultHeader>
        
        <ContactDetails>
          {result.data.phone && (
            <DetailItem>
              <Phone size={14} />
              <span>{result.data.phone}</span>
            </DetailItem>
          )}
          {result.data.email && (
            <DetailItem>
              <Mail size={14} />
              <span>{result.data.email}</span>
            </DetailItem>
          )}
        </ContactDetails>
        
        <ResultFooter>
          <ResultType>
            <User size={14} />
            <span>Contact</span>
          </ResultType>
        </ResultFooter>
      </ContactResultCard>
    );
  }

  return null;
};

const BaseResultCard = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.beige};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.darkGreen};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const TicketResultCard = styled(BaseResultCard)``;

const ContactResultCard = styled(BaseResultCard)``;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const ResultLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const ResultRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 12px;
`;

const ContactName = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: ${colors.darkGreen};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Montserrat, sans-serif;
`;

const Company = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.mutedGold};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 2px;
  font-family: Montserrat, sans-serif;
`;

const UnaddressedDot = styled.div<{ unaddressed: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$unaddressed ? '#FF6B6B' : '#48937F'};
  flex-shrink: 0;
`;

const StatusBadge = styled.div<{ status: string }>`
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  font-family: Montserrat, sans-serif;
  background: ${props => {
    switch (props.$status.toLowerCase()) {
      case 'new': return '#E3F2FD';
      case 'open': return '#FFF3E0';
      case 'closed': return '#F5F5F5';
      default: return '#F5F5F5';
    }
  }};
  color: ${props => {
    switch (props.$status.toLowerCase()) {
      case 'new': return '#1976D2';
      case 'open': return '#F57C00';
      case 'closed': return '#757575';
      default: return '#757575';
    }
  }};
`;

const Message = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 12px;
  line-height: 1.5;
  font-family: Montserrat, sans-serif;
`;

const SenderName = styled.span`
  font-weight: 600;
  color: ${colors.darkGreen};
`;

const ContactDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #666;
  font-family: Montserrat, sans-serif;

  svg {
    stroke: ${colors.mutedGold};
    flex-shrink: 0;
  }
`;

const ResultFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  border-top: 1px solid ${colors.beige};
`;

const ResultType = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.mutedGold};
  font-weight: 500;
  font-family: Montserrat, sans-serif;

  svg {
    stroke: ${colors.mutedGold};
  }
`;

const Timestamp = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #999;
  font-family: Montserrat, sans-serif;
  
  svg {
    stroke: #999;
  }
`;
